import { HttpClient } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppThemeService } from '../theme/app-theme.service';
import { resolveThemeAssetMap } from '../theme/resolve-theme-asset-url';
import type { ThemeAssets } from '../theme/theme.assets.defaults';
import { mapExperienceTokensToTheme } from '../theme/map-theme-tokens';
import type { ExperienceThemeBundle } from '../theme/theme.tokens';
import type { NestedThemeTokens } from '../theme/flatten-theme-tokens';
import {
    isDesignTokensLabEnabled,
} from '../theme/design-tokens-lab.enabled';
import {
    mergeNestedThemeTokens,
    toExperienceThemeDocument,
    type ExperienceThemeDocument,
} from '../theme/design-tokens-profile.util';
import { ThemeProfileService } from '../theme/theme-profile.service';
import {
    DEFAULT_EXPERIENCE_RUBRIC,
    resolveExperienceRubric,
    resolveExperienceTenantScope,
} from './experience-params.util';

/** Survives logout so login can keep last tenant theme+rubric. */
const LAST_THEME_SCOPE_KEY = 'experience_last_theme_scope';

export type ExperienceThemeScope = {
    tenantId: string;
    rubric: string;
};

@Injectable({
    providedIn: 'root',
})
export class ExperienceThemeService {
    private readonly http = inject(HttpClient);
    private readonly appTheme = inject(AppThemeService);
    private readonly injector = inject(Injector);

    /** Last Experience/cache layer before lab profile overlay. */
    private lastBaseExperienceTokens: NestedThemeTokens = {};
    private lastAssetOverrides: Partial<ThemeAssets> = {};
    /** Tokens used for the last apply (export source of truth). */
    private lastMergedTokens: NestedThemeTokens = {};
    /** Asset paths suitable for content-i18n export (not resolved CDN URLs). */
    private lastExportableAssets: Record<string, string> | undefined;
    /** Exportable assets from the Experience/base layer (before lab profile). */
    private lastBaseExportableAssets: Record<string, string> | undefined;
    private lastAppliedScope: ExperienceThemeScope | null = null;

    async load(): Promise<void> {
        this.appTheme.reset();

        if (!environment.experienceThemeEnabled) {
            return;
        }

        const scope = this.resolveThemeScope();

        if (environment.experienceThemeRemoteLoad === false) {
            this.applyLocalExperienceTheme(scope);
            return;
        }

        await this.fetchAndApply(scope);
    }

    async reload(): Promise<void> {
        if (!environment.experienceThemeEnabled) {
            this.appTheme.reset();
            return;
        }

        const scope = this.resolveThemeScope();

        if (environment.experienceThemeRemoteLoad === false) {
            this.appTheme.reset();
            this.applyLocalExperienceTheme(scope);
            return;
        }

        this.clearCacheForScope(scope);
        await this.fetchAndApply(scope);
    }

    /**
     * Re-applies the last Experience/base tokens with the current lab profile (if any).
     * Used by the DEV design-tokens lab when switching profiles without re-fetching.
     */
    reapplyWithCurrentBase(): void {
        if (!environment.experienceThemeEnabled) {
            this.appTheme.reset();
            return;
        }
        const scope = this.lastAppliedScope ?? this.resolveThemeScope();
        this.applyExperienceTokens(
            this.lastBaseExperienceTokens,
            scope,
            this.lastAssetOverrides,
            this.lastBaseExportableAssets,
        );
    }

    /**
     * Experience-compatible theme JSON (`{ tokens, assets? }`).
     * With a lab profile active: exports that tenant overlay (what you publish).
     * On Default: exports the current Experience base layer.
     */
    exportMergedDesignTokensDocument(): ExperienceThemeDocument {
        if (isDesignTokensLabEnabled()) {
            const overlay = this.injector.get(ThemeProfileService).getActiveOverlay();
            if (overlay) {
                return toExperienceThemeDocument(overlay.tokens, overlay.assets);
            }
        }
        return toExperienceThemeDocument(
            this.lastBaseExperienceTokens ?? {},
            this.lastBaseExportableAssets,
        );
    }

    private async fetchAndApply(scope: ExperienceThemeScope): Promise<void> {
        const versionKey = this.versionStorageKey(scope);
        const cacheKey = this.themeStorageKey(scope);

        try {
            const bundle = await firstValueFrom(
                this.http.get<ExperienceThemeBundle>(
                    `${environment.experienceApiBaseUrl}/theme/bundle`,
                    {
                        params: {
                            appId: environment.experienceAppId,
                            tenantId: scope.tenantId,
                            rubric: scope.rubric,
                        },
                    },
                ),
            );

            this.applyBundle(bundle, scope);
            this.rememberLastThemeScope(scope);

            localStorage.setItem(versionKey, bundle.contentVersion);
            localStorage.setItem(
                cacheKey,
                JSON.stringify({
                    contentVersion: bundle.contentVersion,
                    tokens: bundle.theme?.tokens ?? {},
                    assets: bundle.theme?.assets ?? {},
                }),
            );
        } catch (error) {
            console.warn('[ExperienceTheme] Remote load failed', error);
            const cached = localStorage.getItem(cacheKey);
            if (cached) {
                try {
                    const parsed = JSON.parse(cached) as unknown;
                    if (parsed && typeof parsed === 'object') {
                        const record = parsed as Record<string, unknown>;
                        const contentVersion =
                            typeof record['contentVersion'] === 'string'
                                ? record['contentVersion']
                                : undefined;
                        const experienceTokens =
                            record['tokens'] && typeof record['tokens'] === 'object'
                                ? (record['tokens'] as NestedThemeTokens)
                                : (record as NestedThemeTokens);
                        const rawAssets =
                            record['assets'] && typeof record['assets'] === 'object'
                                ? (record['assets'] as Record<string, string>)
                                : undefined;
                        this.applyExperienceTokens(
                            experienceTokens,
                            scope,
                            this.pickKnownAssets(rawAssets, contentVersion),
                            rawAssets,
                        );
                        this.rememberLastThemeScope(scope);
                        return;
                    }
                } catch (parseError) {
                    console.warn('[ExperienceTheme] Cache parse failed', parseError);
                }
            }
            this.applyLocalExperienceTheme(scope);
        }
    }

    private applyBundle(bundle: ExperienceThemeBundle, scope: ExperienceThemeScope): void {
        const experienceTokens = (bundle.theme?.tokens ?? {}) as NestedThemeTokens;
        const rawAssets = (bundle.theme?.assets ?? undefined) as Record<string, string> | undefined;
        const experienceAssets = this.pickKnownAssets(rawAssets, bundle.contentVersion);
        this.applyExperienceTokens(experienceTokens, scope, experienceAssets, rawAssets);
    }

    /** Applies app defaults when remote Experience theme is unavailable / disabled. */
    private applyLocalExperienceTheme(scope: ExperienceThemeScope): void {
        this.applyExperienceTokens({}, scope);
        this.rememberLastThemeScope(scope);
    }

    private applyExperienceTokens(
        experienceTokens: NestedThemeTokens,
        scope: ExperienceThemeScope,
        assetOverrides: Partial<ThemeAssets> = {},
        exportableAssets?: Record<string, string>,
    ): void {
        this.lastBaseExperienceTokens = experienceTokens ?? {};
        this.lastAssetOverrides = assetOverrides ?? {};
        this.lastBaseExportableAssets = exportableAssets;
        this.lastAppliedScope = scope;

        let merged: NestedThemeTokens = experienceTokens ?? {};
        let assets: Partial<ThemeAssets> = { ...assetOverrides };
        let exportAssets = exportableAssets;

        const labOverlay = this.readLabProfileOverlay();
        if (labOverlay) {
            // Same semantics as Experience tenant overlay onto base/rubric theme.
            merged = mergeNestedThemeTokens(experienceTokens ?? {}, labOverlay.tokens);
            if (labOverlay.assets) {
                assets = {
                    ...assets,
                    ...(labOverlay.assets as Partial<ThemeAssets>),
                };
                exportAssets = {
                    ...(exportableAssets ?? {}),
                    ...(labOverlay.assets as Record<string, string>),
                };
            }
        }

        this.lastMergedTokens = merged ?? {};
        this.lastExportableAssets = exportAssets;
        const { tokens, cssVarOverrides } = mapExperienceTokensToTheme(merged);
        this.appTheme.applyTokens(tokens, cssVarOverrides, assets);
    }

    private readLabProfileOverlay(): {
        tokens: NestedThemeTokens;
        assets?: Partial<ThemeAssets> | Record<string, string>;
    } | null {
        if (!isDesignTokensLabEnabled()) {
            return null;
        }
        return this.injector.get(ThemeProfileService).getActiveOverlay();
    }

    private pickKnownAssets(
        raw: Record<string, string> | Partial<ThemeAssets> | undefined,
        contentVersion?: string,
    ): Partial<ThemeAssets> {
        return resolveThemeAssetMap(raw as Record<string, string> | undefined, {
            experienceApiBaseUrl: environment.experienceApiBaseUrl,
            contentVersion,
            cdnBaseUrl: (environment as { experienceCdnBaseUrl?: string }).experienceCdnBaseUrl,
            storagePrefix: (environment as { experienceContentStoragePrefix?: string })
                .experienceContentStoragePrefix,
        });
    }

    /**
     * Prefer active session tenant+rubric; if logged out, reuse last successful theme scope
     * so the login screen keeps the branding from the previous session.
     */
    private resolveThemeScope(): ExperienceThemeScope {
        const session = this.readSessionThemeScope();
        if (session) {
            return session;
        }
        const last = this.readLastThemeScope();
        if (last) {
            return last;
        }
        return { tenantId: 'default', rubric: DEFAULT_EXPERIENCE_RUBRIC };
    }

    private readSessionThemeScope(): ExperienceThemeScope | null {
        try {
            const tenantRaw = localStorage.getItem('tenant');
            if (!tenantRaw) {
                return null;
            }
            const tenant = JSON.parse(tenantRaw) as {
                code?: string;
                id?: number | string;
                experienceRubric?: string;
            } | null;
            if (!tenant) {
                return null;
            }
            const userRaw = localStorage.getItem('user');
            const user = userRaw
                ? (JSON.parse(userRaw) as { rubric?: string } | null)
                : null;
            return {
                tenantId: resolveExperienceTenantScope(tenant),
                rubric: resolveExperienceRubric(tenant.experienceRubric, user?.rubric),
            };
        } catch {
            return null;
        }
    }

    private rememberLastThemeScope(scope: ExperienceThemeScope): void {
        if (!scope?.tenantId) {
            return;
        }
        localStorage.setItem(
            LAST_THEME_SCOPE_KEY,
            JSON.stringify({
                tenantId: scope.tenantId,
                rubric: scope.rubric || DEFAULT_EXPERIENCE_RUBRIC,
            }),
        );
    }

    private readLastThemeScope(): ExperienceThemeScope | null {
        try {
            const raw = localStorage.getItem(LAST_THEME_SCOPE_KEY);
            if (!raw) {
                return null;
            }
            const parsed = JSON.parse(raw) as Partial<ExperienceThemeScope> | null;
            if (!parsed?.tenantId || typeof parsed.tenantId !== 'string') {
                return null;
            }
            return {
                tenantId: parsed.tenantId,
                rubric:
                    typeof parsed.rubric === 'string' && parsed.rubric.trim()
                        ? parsed.rubric.trim()
                        : DEFAULT_EXPERIENCE_RUBRIC,
            };
        } catch {
            return null;
        }
    }

    private clearCacheForScope(scope: ExperienceThemeScope): void {
        localStorage.removeItem(this.versionStorageKey(scope));
        localStorage.removeItem(this.themeStorageKey(scope));
    }

    private versionStorageKey(scope: ExperienceThemeScope): string {
        return `experience_theme_version_${environment.experienceAppId}_${scope.rubric}_${scope.tenantId}`;
    }

    private themeStorageKey(scope: ExperienceThemeScope): string {
        return `experience_theme_cache_${environment.experienceAppId}_${scope.rubric}_${scope.tenantId}`;
    }
}

export function initExperienceTheme(service: ExperienceThemeService): () => Promise<void> {
    return () => service.load();
}
